import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { UserRole } from '@/types/auth';

interface User {
  id: string;
  email: string;
  full_name: string;
  phone_number?: string;
  tenant_id?: string;
  tenant_name?: string;
  role?: string;
  roles?: UserRole[];
  activeRole?: UserRole;
  user_type: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export function useAuth() {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeRole, setActiveRoleState] = useState<UserRole | null>(null);

  const hasPermission = (_permission: string): boolean => {
    if (!user) return false;
    // Basic check — extend with role-permission mapping if needed
    return true;
  };

  const setActiveRole = (role: UserRole) => {
    setActiveRoleState(role);
    if (typeof window !== 'undefined') {
      localStorage.setItem('active_role', role);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      // Import API client dynamically
      const { apiClient } = await import('@/lib/api-client');

      // Verify token by fetching user info
      const userData = await apiClient.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      // Token invalid or expired
      console.error('Auth check failed:', error);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('tenant_id');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const { apiClient } = await import('@/lib/api-client');
      await apiClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      router.push('/login');
      router.refresh();
    }
  };

  return {
    user,
    loading,
    isLoading: loading,
    isAuthenticated,
    logout,
    checkAuth,
    hasPermission,
    setActiveRole,
    activeRole,
  };
}

export function useRequireAuth() {
  const router = useRouter();
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      router.push('/login');
    }
  }, [isAuthenticated, loading, router]);

  return { isAuthenticated, loading };
}
